import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(iso: string): string {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(iso));
}

export function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

export function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export function truncate(str: string, maxLength: number): string {
  return str.length > maxLength ? `${str.slice(0, maxLength)}…` : str;
}

/**
 * Type guard to ensure region is always a string array.
 * Handles legacy cases where region might be a string or mixed type.
 * Used in CSV export and data transformations to prevent CSV corruption.
 */
export function ensureRegionArray(region: unknown): string[] {
  if (Array.isArray(region)) {
    return region.filter((r) => typeof r === "string");
  }
  if (typeof region === "string" && region) {
    return [region];
  }
  return [];
}

function splitRows(text: string): string[] {
  const rows = text.replace(/\r\n?/g, "\n").split("\n");
  while (rows.length && rows[rows.length - 1].trim() === "") rows.pop();
  return rows;
}

/**
 * True when clipboard text looks like a copied spreadsheet range: at least two
 * rows, every non-empty row tab-delimited. A single tabbed line is ambiguous
 * with ordinary text, so it is deliberately not treated as a table.
 */
export function isTabularText(text: string): boolean {
  const rows = splitRows(text);
  if (rows.length < 2) return false;
  const nonEmpty = rows.filter((r) => r.trim() !== "");
  return nonEmpty.length >= 2 && nonEmpty.every((r) => r.includes("\t"));
}

function csvCell(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export type PreviewKind = "image" | "pdf" | "text";

/** Which inline preview (if any) a file's content-type supports. */
export function previewKind(contentType: string): PreviewKind | null {
  if (contentType.startsWith("image/")) return "image";
  if (contentType === "application/pdf") return "pdf";
  if (contentType === "text/csv" || contentType === "text/plain") return "text";
  return null;
}

export function tsvToCsv(text: string): string {
  return splitRows(text)
    .map((row) => row.split("\t").map(csvCell).join(","))
    .join("\n");
}

/** RFC 4180-style parse: quoted cells may contain commas, newlines and "" escapes. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  const src = text.replace(/\r\n?/g, "\n");

  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          quoted = false;
        }
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n") {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += ch;
    }
  }
  if (cell !== "" || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}
